import { useEffect } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';

export default function useGlobalStyles() {
  const { colors } = useTheme();

  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      // Add viewport meta tag to prevent zooming on mobile
      let viewport = document.querySelector('meta[name=viewport]');
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.name = 'viewport';
        document.head.appendChild(viewport);
      }
      viewport.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0, viewport-fit=cover';

      const style = document.createElement('style');
      style.textContent = `
        /* PWA Full Screen Styles */
        html, body, #root {
          width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          -webkit-overflow-scrolling: touch !important;
        }
        
        /* Ensure the app container fills the screen */
        #root > div {
          min-height: 100% !important;
          height: 100% !important;
        }

        /* PWA: keep Radix poppers below status bar */
        @media (display-mode: standalone) {
          [data-radix-popper-content-wrapper] {
            margin-top: var(--pwa-safe-area-top, 0px) !important;
          }
        }

        button[aria-haspopup="menu"] {
          outline: none !important;
          border: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
          background: transparent !important;
          background-color: transparent !important;
          color: inherit !important;
        }
        @media (max-width: 768px) {
          div[data-radix-popper-content-wrapper],
          div[data-radix-menu-content] {
            max-width: 90vw !important;
          }
        }
        svg[tabindex="-1"] {
          outline: none !important;
        }
        input:focus,
        button:focus {
          outline: none !important;
        }

        div[aria-label="Bottom Sheet"][role="slider"] {
          padding-bottom: 0 !important;
        }
          
        /* ***************************************************** 
        Scrollbar - Thin themed scrollbars for desktop
        Forces always-visible classic scrollbars instead of
        macOS overlay scrollbars (which require a scroll wheel
        to reveal). Keeps things usable on trackpad-only laptops.
        !important overrides react-native-web's atomic
        scrollbar-width:none class applied when
        showsVerticalScrollIndicator={false} (use useResponsiveness().showsScrollIndicator on web desktop).
        ***************************************************** */

        * {
          scrollbar-width: thin !important;
          scrollbar-color: ${colors.border_color_secondary} transparent !important;
        }

        *::-webkit-scrollbar {
          width: 6px !important;
          height: 6px !important;
          /* RN Web can set display:none on scroll views with higher specificity than *; keep thumb interactive */
          display: block !important;
        }

        *::-webkit-scrollbar-track {
          background: transparent;
        }

        *::-webkit-scrollbar-thumb {
          background: ${colors.border_color_secondary};
          border-radius: 3px;
        }

        *::-webkit-scrollbar-thumb:hover {
          background: ${colors.text_color_tertiary};
        }

        [data-no-scrollbar] {
          scrollbar-width: none !important;
        }
        [data-no-scrollbar]::-webkit-scrollbar {
          display: none !important;
        }

        /* ***************************************************** 
        Dropdown Menu
        ***************************************************** */

        div[data-radix-popper-content-wrapper] {
          border-radius: 16px !important;
          box-shadow: none !important;
        }
        // div[data-radix-popper-content-wrapper]:after {
        //   content: '';
        //   height: 0px;
        //   width: 0px;
        //   background-color: transparent;
        //   position: absolute;
        //   border-top: 10px solid ${colors.border_color_secondary};
        //   border-right: 10px solid transparent;
        //   border-left: 10px solid transparent;
        //   border-bottom: 10px solid transparent;
        //   margin-left: calc(50% - 5px);
        // }
        .DropdownMenuContent,
        .DropdownMenuSubContent {
          background-color: ${colors.solid_color_primary} !important;
          border: 1px solid ${colors.border_color_secondary} !important;
          max-height: 77vh !important;
          overflow-y: auto !important;
        }
        /* Model selector dropdown needs special handling for height */
        .DropdownMenuContent.ModelSelectorDropdown {
          max-height: none !important;
          overflow: hidden !important;
        }
        .DropdownMenuSeparator {
          background-color: ${colors.solid_color_tertiary} !important;
        }
        .DropdownMenuItem,
        .DropdownMenuCheckboxItem,
        .DropdownMenuRadioItem,
        .DropdownMenuSubTrigger,
        .DropdownMenuItemIcon {
          color: ${colors.text_color_primary} !important;
          margin-bottom:1px;
        }
        .DropdownMenuItemIcon * {
          color: ${colors.text_color_primary};
        }
        .DropdownMenuItemIcon.DropdownMenuItemIconAccent,
        .DropdownMenuItemIcon.DropdownMenuItemIconAccent * {
          color: ${colors.accent_text_color} !important;
          fill: ${colors.accent_text_color} !important;
        }
        .DropdownMenuItemIcon.DropdownMenuItemIconDanger,
        .DropdownMenuItemIcon.DropdownMenuItemIconDanger * {
          color: ${colors.color_destructive} !important;
          fill: ${colors.color_destructive} !important;
        }
        .DropdownMenuItem.DropdownMenuItemSecondary,
        .DropdownMenuItem:not([data-auth="true"]):hover,
        .DropdownMenuItem:not(.DropdownMenuItemDisabled):hover,
        .DropdownMenuCheckboxItem:hover,
        .DropdownMenuRadioItem:hover {
          background-color: ${colors.solid_color_tertiary} !important;
        }
        .DropdownMenuItem[data-auth="true"]:hover {
          opacity: 0.9;
        }
        .DropdownMenuItem.DropdownMenuItemSecondary:hover {
          background-color: ${colors.solid_color_secondary} !important;
        }
        .DropdownMenuItem.DropdownMenuItemActive,
        .DropdownMenuItem.DropdownMenuItemActive:hover,
        .DropdownMenuCheckboxItem[data-state="checked"]:hover,
        .DropdownMenuCheckboxItem[data-state="checked"] {
          background-color: ${colors.accent_color_primary} !important;
        }
        .DropdownMenuItem.DropdownMenuItemActive,
        .DropdownMenuItem.DropdownMenuItemActive:hover,
        .DropdownMenuItem.DropdownMenuItemActive .DropdownMenuItemTitle,
        .DropdownMenuItem.DropdownMenuItemActive .DropdownMenuItemTitle *,
        .DropdownMenuItem.DropdownMenuItemActive .DropdownMenuItemIcon,
        .DropdownMenuCheckboxItem[data-state="checked"]:hover,
        .DropdownMenuCheckboxItem[data-state="checked"],
        .DropdownMenuCheckboxItem[data-state="checked"] * {
          color: ${colors.accent_text_color} !important;
        }
        .DropdownMenuLabel div {
          padding-left: 0 !important;
        }
        .DropdownMenuContent::-webkit-scrollbar {
          width: 8px;
        }
        .DropdownMenuContent::-webkit-scrollbar-track {
          background: transparent;
        }
        .DropdownMenuContent::-webkit-scrollbar-thumb {
          background: ${colors.border_color_secondary};
          border-radius: 4px;
        }
        .DropdownMenuContent::-webkit-scrollbar-thumb:hover {
          background: ${colors.text_color_tertiary};
        }
        .DropdownMenuItem.DropdownMenuItemDanger:hover {
          background-color: #FF3B30!important;
        }
        .DropdownMenuItem.DropdownMenuItemDanger:hover .DropdownMenuItemTitle,
        .DropdownMenuItem.DropdownMenuItemDanger:hover .DropdownMenuItemTitle * {
          color: #fff!important;
        }
        .DropdownMenuItem.DropdownMenuItemDanger:hover .DropdownMenuItemIcon *,
        .DropdownMenuItem.DropdownMenuItemDanger:hover .DropdownMenuItemIcon,
        .DropdownMenuItem.DropdownMenuItemDanger:hover .DropdownMenuItemIcon svg {
          color: #fff!important;
          fill: #fff!important;
        }
        .DropdownMenuItem.DropdownMenuItemDisabled,
        .DropdownMenuItem.DropdownMenuItemDisabled:hover {
          background-color: transparent !important;
          cursor: disabled !important;
        }
        .DropdownMenuItem.DropdownMenuItemDisabled .DropdownMenuItemIcon,
        .DropdownMenuItem.DropdownMenuItemDisabled .DropdownMenuItemIcon * {
          color: ${colors.text_color_tertiary} !important;
          fill: ${colors.text_color_tertiary} !important;
        }
        .DropdownMenuItem.DropdownMenuItemDisabled .DropdownMenuItemTitle,
        .DropdownMenuItem.DropdownMenuItemDisabled .DropdownMenuItemTitle * {
          color: ${colors.text_color_tertiary} !important;
        }
        .DropdownMenuItem.DropdownMenuItemWithSubtitle {
          height: auto !important;
          padding-top: 8px !important;
          padding-bottom: 8px !important;
        }
        .DropdownMenuItem.DropdownMenuItemWithSubtitle .DropdownMenuItemIcon {
          margin-bottom: auto !important;
        }
        .DropdownMenuItemTitle {
          flex: 1;
        }
        .DropdownMenuItemTitle.DropdownMenuItemTitleWithSubtitle {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 4px !important;
        }

        /* ***************************************************** 
        Message Bubble
        ***************************************************** */

        /* Enable text selection for all message content */
        .message-bubble-wrapper,
        .message-bubble-wrapper * {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }

        /* Controls inside an answer are actions, not selectable prose. This
           must override the message-wide selection rule above, including
           nested React Native Web Text elements. Editable inputs remain text. */
        .message-bubble-wrapper :is(button, [role="button"], [role="radio"], [role="checkbox"], [role="switch"], [role="tab"], [role="slider"]),
        .message-bubble-wrapper :is(button, [role="button"], [role="radio"], [role="checkbox"], [role="switch"], [role="tab"], [role="slider"]) * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }

        /* Touch browsers keep actions visible without tapping the message. */
        .message-bubble-wrapper[data-is-animated="true"]:not(.error) {
          padding-bottom: 34px !important;
        }
        .message-bubble-wrapper[data-is-animated="true"]:not(.error) .message-bubble-actions-container {
          height: 34px !important;
          opacity: 1 !important;
          transform: translateY(0) !important;
          pointer-events: auto !important;
        }
        @media (hover: hover) and (pointer: fine) {
          .message-bubble-wrapper[data-is-animated="true"]:not(.error) .message-bubble-actions-container {
            height: 34px !important;
            opacity: 0 !important;
            transform: translateY(3px) !important;
            pointer-events: none !important;
            transition: opacity 180ms cubic-bezier(0.2, 0, 0, 1), transform 180ms cubic-bezier(0.2, 0, 0, 1) !important;
            will-change: opacity, transform;
          }
          .message-bubble-wrapper[data-is-animated="true"]:not(.error):hover .message-bubble-actions-container,
          .message-bubble-wrapper[data-is-animated="true"]:not(.error):focus-within .message-bubble-actions-container {
            opacity: 1 !important;
            transform: translateY(0) !important;
            pointer-events: auto !important;
          }
        }
        .message-bubble-wrapper table {
          border-collapse: collapse;
          width: 100%;
        }
        .message-bubble-wrapper table thead {
          background-color: ${colors.solid_color_primary};
          border-bottom-width: 1px;
          border-bottom-color: ${colors.border_color_input};
        }
        .message-bubble-wrapper table thead tr {
          border-bottom-width: 1px;
          border-bottom-color: ${colors.border_color_input};
        }
        .message-bubble-wrapper table thead tr th {
          border-bottom: 1px solid ${colors.border_color_input};
          border-right: 1px solid ${colors.border_color_input};
          background-color: ${colors.solid_color_primary};
          font-weight: 600;
        }
        .message-bubble-wrapper table thead tr th * {
          font-weight: 600;
        }
        .message-bubble-wrapper table tbody tr {
          border-bottom: 1px solid ${colors.border_color_input};
        }
        .message-bubble-wrapper table tbody tr:last-child {
          border-bottom: none;
        }
        .message-bubble-wrapper table tbody tr td {
          border-right: 1px solid ${colors.border_color_input};
        }
        .message-bubble-wrapper table thead tr th:last-child,
        .message-bubble-wrapper table tbody tr td:last-child {
          border-right: none;
        }
        .message-bubble-wrapper ul,
        .message-bubble-wrapper ol {
          padding-left: 24px;
          margin-top: 6px;
          margin-bottom: 6px;
        }
        .message-bubble-wrapper li {
          display: list-item !important;
          margin-top: 4px;
          margin-bottom: 4px;
        }
        .message-bubble-wrapper ul > li {
          list-style-type: disc;
        }
        .message-bubble-wrapper ol > li {
          list-style-type: decimal;
        }
        @keyframes mentionHighlight {
          0% {
            transform: scale(1);
            opacity: 0.5 !important;
            background-color: rgba(255, 0, 0, 0.1);
          }
          50% {
            transform: scale(1.05);
            opacity: 0.5 !important;
            background-color: rgba(255, 0, 0, 0.5);
          }
          100% {
            transform: scale(1);
            opacity: 0.5 !important;
            background-color: rgba(255, 0, 0, 0.1);
          }
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
        viewport.content = 'width=device-width, initial-scale=1';
      };
    }
  }, [colors]);
}
